import React, { useState } from 'react';

import { c } from 'ttag';

import { Badge, Button } from '@freelancelabs/components';

import Card from '../../components/Card';
import { PortalUserQuery } from '../../graphql/__generated__/types';
import GrantDashboardDialog from './GrantDashboardDialog';
import RevokeDashboardDialog from './RevokeDashboardDialog';
import { blockedNotice, inactiveAccessSentence } from './dashboards';
import { EMPTY, formatDate } from './display';
import { statusCategory, statusLabel } from './status';

type Membership = NonNullable<NonNullable<PortalUserQuery['portalUser']>['memberships']>[number];
type Granted = Membership['dashboards'][number];

interface Props {
    memberships: Membership[];
    personName: string;
}

/**
 * The dashboards of one person, **grouped by the membership each hangs from**:
 * PCG-106.
 *
 * **Attention, this grouping is what makes the card true, and flattening it would be the one
 * mistake to avoid.** The sheet describes a person, but an habilitation is not held by
 * a person: it is held by a **membership**, and the API deliberately puts `dashboards`
 * there, beside the visible markets. Two accounts of the same group may declare an
 * entry carrying the same label (the API has a test for exactly that) so a flat list
 * would show « Consommation 2026 » twice with nothing saying which account each is
 * granted under, and the withdrawal would have no membership to name.
 *
 * **Attention, nothing is hidden, and a blocked habilitation is the reason why.** An entry
 * archived since it was granted, a module closed on the account, a perimeter never
 * declared: the line survives on purpose in all three cases, it is what answers
 * « qui l'avait », and hiding it would answer « on ne vous l'a jamais accordé » to
 * somebody asking why their dashboard stopped opening. Each says which of the three it
 * is, because each calls for a different gesture.
 *
 * **Attention, the fourth condition is said per account and not per row.** The token maker also
 * requires an **active** membership, and the API left that out of `blockedReason` on
 * purpose: it belongs to the membership, which the card already names. So it is stated
 * once under the account heading, and no row claims to open while it holds.
 *
 * **An empty card is the ordinary state**, and it is written rather than left blank:
 * the catalogue is a whitelist entered by hand, so most people hold nothing at all.
 */
const DashboardsCard = ({ memberships, personName }: Props) => {
    const [granting, setGranting] = useState<string | null>(null);
    /**
     * **Attention, the entry is held whole, where the reopening dialog deliberately holds an
     * id**, and the inversion is the difference between the two gestures. Reopening
     * updates a row that survives, so holding the object would keep rendering it as it
     * was before the refetch; withdrawing **deletes** it, so an id would resolve to
     * nothing the moment the refetch lands and the dialog would vanish in the middle of
     * its own confirmation: the confirmation that says where the grant date and its
     * author went.
     */
    const [revoking, setRevoking] = useState<{ membershipId: string; entry: Granted } | null>(null);

    const total = memberships.reduce((sum, membership) => sum + (membership.dashboards ?? []).length, 0);

    const grantingMembership = memberships.find((membership) => membership.membershipId === granting);
    const revokingMembership = memberships.find((membership) => membership.membershipId === revoking?.membershipId);

    return (
        <>
            <Card
                title={c('User dashboards').t`Dashboards`}
                // The count of what is held, over the whole list the API returned:
                // this list has no pages, so its length is the complete set.
                counter={total}
            >
                <div className="flex flex-col gap-6">
                    {memberships.map((membership) => {
                        const dashboards = membership.dashboards ?? [];
                        const membershipId = membership.membershipId;
                        const inactive = inactiveAccessSentence(membership.status);

                        return (
                            <div key={membershipId}>
                                <div className="flex flex-wrap items-center gap-2">
                                    <h3 className="text-sm font-semibold text-text-900">
                                        {membership.organizationName}
                                    </h3>
                                    {membership.status && (
                                        <Badge category={statusCategory(membership.status)} size="small">
                                            {statusLabel(membership.status)}
                                        </Badge>
                                    )}
                                    {/* Left visible whatever the account carries: the
                                        screen cannot know whether this operator holds
                                        `manage_users`: `viewer` exposes the role and
                                        not the permissions, so the refusal is rendered
                                        after the click, naming the right that is
                                        missing. */}
                                    <Button
                                        className="ml-auto"
                                        category="weak"
                                        size="small"
                                        disabled={!membershipId}
                                        onClick={() => setGranting(membershipId ?? null)}
                                    >
                                        {c('User dashboards').t`Grant a dashboard…`}
                                    </Button>
                                </div>

                                {/* The membership's own condition, said once for the
                                    whole block. It is not a reason of the dashboard,
                                    which is why it is not one of the three below. */}
                                {inactive && <p className="mt-1.5 text-xs text-text-600">{inactive}</p>}

                                {dashboards.length === 0 ? (
                                    <p className="mt-1.5 text-xs text-text-600">
                                        {c('User dashboards')
                                            .t`No dashboard is granted on this account. That is the ordinary state rather than a gap: the catalogue is a whitelist entered by hand, and nothing is granted until somebody grants it.`}
                                    </p>
                                ) : (
                                    <ul className="mt-3 flex flex-col gap-3">
                                        {dashboards.map((dashboard) => {
                                            const blocked = blockedNotice(dashboard.blockedReason);
                                            // Computed before the template: `ttag` refuses a call
                                            // expression inside a localized string, and the
                                            // extraction fails rather than shipping English.
                                            const grantedOn = formatDate(dashboard.grantedAt);
                                            const grantedBy = dashboard.grantedByName || EMPTY;
                                            return (
                                                <li
                                                    key={dashboard.dashboardId}
                                                    className="flex flex-wrap items-start gap-x-3 gap-y-1.5 border-b border-border-weak pb-3 last:border-b-0 last:pb-0"
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="text-sm font-medium text-text-900">
                                                                {dashboard.name}
                                                            </span>
                                                            {blocked ? (
                                                                <Badge category={blocked.tone} size="small">
                                                                    {blocked.tag}
                                                                </Badge>
                                                            ) : (
                                                                // Attention, only where the access is
                                                                // active: `opens` is the API's
                                                                // judgement on the dashboard
                                                                // alone, and a green « ouvre »
                                                                // over a closed access would
                                                                // contradict the sentence above.
                                                                !inactive && (
                                                                    <Badge category="success" size="small">
                                                                        {c('User dashboards').t`Opens`}
                                                                    </Badge>
                                                                )
                                                            )}
                                                        </div>
                                                        {blocked && (
                                                            <p className="mt-1 text-xs text-text-600">
                                                                {blocked.sentence}
                                                            </p>
                                                        )}
                                                        {/* Both facts die with the line:
                                                            the withdrawal deletes it, and
                                                            they survive only in the journal.
                                                            Printed here while they exist. */}
                                                        <p className="mt-1 text-xs text-text-500">
                                                            {c('User dashboards')
                                                                .t`Granted on ${grantedOn} by ${grantedBy}`}
                                                        </p>
                                                    </div>
                                                    <Button
                                                        category="weak"
                                                        size="small"
                                                        disabled={!membershipId}
                                                        onClick={() =>
                                                            setRevoking({
                                                                membershipId: membershipId as string,
                                                                entry: dashboard,
                                                            })
                                                        }
                                                    >
                                                        {c('User dashboards').t`Withdraw`}
                                                    </Button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Card>

            {grantingMembership?.membershipId && grantingMembership.organizationId && (
                <GrantDashboardDialog
                    membershipId={grantingMembership.membershipId}
                    organizationId={grantingMembership.organizationId}
                    organizationName={grantingMembership.organizationName ?? EMPTY}
                    personName={personName}
                    heldDashboardIds={(grantingMembership.dashboards ?? []).map((entry) => entry.dashboardId)}
                    inactiveAccess={inactiveAccessSentence(grantingMembership.status)}
                    onClose={() => setGranting(null)}
                />
            )}

            {revoking && revokingMembership && (
                <RevokeDashboardDialog
                    membershipId={revoking.membershipId}
                    organizationName={revokingMembership.organizationName ?? EMPTY}
                    personName={personName}
                    entry={revoking.entry}
                    onClose={() => setRevoking(null)}
                />
            )}
        </>
    );
};

export default DashboardsCard;
